/**
 * Sheet
 *
 * A light sheet over the app (DESIGN_SYSTEM 1): the page grey with top
 * radius 34 (`Sheet/iPhone/Top Radius`, FM-2), Cancel on the leading edge
 * of its bar and the title centred (SH-8). Backdrop tap and Escape dismiss,
 * as the Cancel button does (MO-4). On a larger screen it is a centred
 * panel with the same radius all round.
 *
 * The call's own sheets (CallInviteModal) draw the same shell on the call
 * canvas; this is its counterpart for the app's light screens.
 */

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface SheetProps {
  title: string;
  onClose: () => void;
  /** Disables dismissal while a request is in flight. */
  busy?: boolean;
  /** "Close" where the sheet's own action is a cancel (SH-6 allows either). */
  dismissLabel?: 'Cancel' | 'Close';
  /** No dismiss button: the sheet's own buttons close it (Escape and the backdrop still do). */
  hideDismiss?: boolean;
  /** The bar's trailing action, opposite the dismiss button: a Save that is never below the fold. */
  action?: ReactNode;
  children: ReactNode;
}

export default function Sheet({ title, onClose, busy = false, dismissLabel = 'Cancel', hideDismiss = false, action, children }: SheetProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // In a portal above the app's bars: the phone tab bar is z-index 9999
  // (index.css, #mobile-bottom-nav), so the sheet sits just over it.
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end justify-center md:items-center md:p-6"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-[34px] bg-db-bg md:rounded-[34px]"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* The title may take two lines; the bar grows with it. px-20 keeps it clear of both buttons. */}
        <div className="relative flex min-h-14 flex-shrink-0 items-center justify-center px-20 py-2">
          {!hideDismiss && (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="ios-press absolute left-2 flex h-11 cursor-pointer items-center rounded-full px-3 text-[17px] leading-[22px] text-biscayne disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
            >
              {dismissLabel}
            </button>
          )}
          <h2 className="[overflow-wrap:anywhere] text-center text-[17px] leading-[22px] font-semibold text-db-text-1">{title}</h2>
          {action && <div className="absolute right-2 flex h-11 items-center">{action}</div>}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
